import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { type Appeal, appealsApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatDateTime, truncate } from '@/lib/utils';

export function AppealsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedAppeal, setSelectedAppeal] = useState<Appeal | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['appeals', { status: statusFilter }],
    queryFn: () => appealsApi.list({ status: statusFilter || undefined }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { status: 'approved' | 'rejected'; admin_response?: string };
    }) => appealsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      queryClient.invalidateQueries({ queryKey: ['bans'] });
      setSelectedAppeal(null);
    },
  });

  return (
    <Layout title="Апелляции" subtitle={`Всего: ${data?.total ?? 0}`}>
      {/* Filters */}
      <div className="cyber-card p-3 mb-4">
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', ''].map((status) => (
            <button
              key={status || 'all'}
              className={`cyber-button text-sm ${statusFilter === status ? 'cyber-button-primary' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status === ''
                ? 'Все'
                : status === 'pending'
                  ? 'Ожидают'
                  : status === 'approved'
                    ? 'Одобрены'
                    : 'Отклонены'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="cyber-card overflow-hidden">
          <table className="cyber-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Ban ID</th>
                <th>Сообщение</th>
                <th>Статус</th>
                <th>Создана</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.appeals.map((appeal) => (
                <tr key={appeal.id}>
                  <td className="font-mono text-neon-cyan">#{appeal.id}</td>
                  <td className="font-mono">#{appeal.ban_id}</td>
                  <td className="max-w-xs text-gray-300">{truncate(appeal.message, 50)}</td>
                  <td>
                    <StatusBadge status={appeal.status} type="appeal" />
                  </td>
                  <td className="text-gray-400 text-sm">{formatDateTime(appeal.created_at)}</td>
                  <td>
                    <button
                      className="cyber-button text-sm"
                      onClick={() => setSelectedAppeal(appeal)}
                    >
                      Открыть
                    </button>
                  </td>
                </tr>
              ))}
              {(!data?.appeals || data.appeals.length === 0) && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    Апелляции не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedAppeal && (
        <AppealModal
          appeal={selectedAppeal}
          onClose={() => setSelectedAppeal(null)}
          onUpdate={(data) => updateMutation.mutate({ id: selectedAppeal.id, data })}
          isUpdating={updateMutation.isPending}
        />
      )}
    </Layout>
  );
}

interface AppealModalProps {
  appeal: Appeal;
  onClose: () => void;
  onUpdate: (data: { status: 'approved' | 'rejected'; admin_response?: string }) => void;
  isUpdating: boolean;
}

function AppealModal({ appeal, onClose, onUpdate, isUpdating }: AppealModalProps) {
  const [response, setResponse] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="cyber-card w-full max-w-lg">
        {/* Header */}
        <div className="p-4 border-b border-cyber-light">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-display text-white">
              Апелляция <span className="text-neon-cyan">#{appeal.id}</span>
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-gray-500 uppercase">Сообщение пользователя</label>
            <p className="text-sm text-gray-200 whitespace-pre-wrap mt-1">{appeal.message}</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase">Статус</label>
            <div className="mt-1">
              <StatusBadge status={appeal.status} type="appeal" />
            </div>
          </div>

          {appeal.status === 'pending' && (
            <div>
              <label className="text-xs text-gray-500 uppercase">Ответ модератора</label>
              <textarea
                className="cyber-input mt-1 h-20 resize-none text-sm"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Добавить ответ (необязательно)..."
              />
            </div>
          )}

          {appeal.admin_response && (
            <div>
              <label className="text-xs text-gray-500 uppercase">Ответ модератора</label>
              <p className="text-sm text-gray-200 mt-1">{appeal.admin_response}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-cyber-light flex items-center justify-between">
          {appeal.status === 'pending' ? (
            <div className="flex gap-2">
              <button
                className="cyber-button-success flex items-center gap-2 text-sm"
                onClick={() =>
                  onUpdate({ status: 'approved', admin_response: response || undefined })
                }
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" /> Одобрить
                  </>
                )}
              </button>
              <button
                className="cyber-button-danger flex items-center gap-2 text-sm"
                onClick={() =>
                  onUpdate({ status: 'rejected', admin_response: response || undefined })
                }
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <XCircle className="w-3.5 h-3.5" /> Отклонить
                  </>
                )}
              </button>
            </div>
          ) : (
            <div />
          )}
          <button className="cyber-button text-sm" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
